import React, {createRef, useContext, useEffect, useRef, useMemo, useState} from 'react';
import moment from 'moment';
import axios from 'axios';
import {useHistory} from 'react-router-dom';
import config from '../../config.js';
import UserContext from '../common/UserContext.js';

// reactstrap
import {Col, Row} from 'reactstrap';

// css
import '../../assets/css/EARequestLicense.css';

// components
import MyProductsGrid from './AssignProducts/MyProductsGrid.js';
import HostGrid from './AssignProducts/HostGrid.js';
import CopyHostsGrid from './AssignProducts/CopyHostsGrid.js';
import ProductGrid from './AssignProducts/ProductGrid.js';
import CopyProductGrid from './AssignProducts/CopyProductGrid.js';
import AssignProductsModal from '../EARequestLicense/AssignProducts/AssignProductsModal.js';
import DiscardChangesModal from '../EARequestLicense/AssignProducts/DiscardChangesModal.js';
import Alert from '../common/Alert.js';

// kendo react
import {Button} from '@progress/kendo-react-buttons';
import {PanelBar, PanelBarItem} from '@progress/kendo-react-layout';
import {RadioGroup} from '@progress/kendo-react-inputs';
import {Tooltip} from '@progress/kendo-react-tooltip';
import {ExcelExport, ExcelExportColumn} from '@progress/kendo-react-excel-export';

// multilingual
import {useLocalization} from '@progress/kendo-react-intl';
import {Text} from '../common/MultilingualText.js';
import {
    contactUsHeaderKey,
    genericErrorTitleKey,
    productsHostKey,
    durationIncreaseCostKey,
    poolOverKey,
    exportSelectionsKey,
    endDateNotPossibleKey,
    endDateExceedPoolKey,
    continueKey,
    mainMessages,
    myCartKey,
    saveChangesKey,
    quantityConsumedSessionKey,
    quantityConsumedKey,
    assignQtyKey,
    costPerUnitKey,
    descriptionKey,
    endDateKey,
    hostIdKey,
    licenseTypeKey,
    productNumberKey,
    startDateKey,
    totalCostKey,
    myAssignedProductsKey,
    discardChangesKey,
    quantityZeroKey,
    groupByProductsKey,
    groupByHostIdKey,
    backKey,
} from "../../assets/text/MultilingualText.js";
import {multiplyMoney} from "../common/utilities";
import {fileExcelIcon} from "@progress/kendo-svg-icons";
import PanelBarHeader from "./AssignProducts/PanelBarHeader";
import getScalingFactor from "./AssignProducts/ScalingFactor";


const AssignProducts = (props) => {
    const history = useHistory();
    const {
        isLoading,
        setIsLoading,
        stepperItems,
        setStepperItems,
        changeStepActivation,
        unsavedAssignedChanges,
        setUnsavedAssignedChanges,
        selectedProducts,
        setSelectedProducts,
        assignedProducts,
        setAssignedProducts,
        remixType,
        scalingFactor,
        headerInfo,
        setHeaderInfo,
        saveAssignments,
        saveRemovals,
        eAMinDuration,
        futureSubPoolValues,
        setFutureSubPoolValues,
        setUnSavedPool,
        showQuantityConsumedError,
        saveAssignmentsForCopyProductsFlow,
        showDiscardChanges,
        fromRequestAgain
    } = props;
    const {
        accessToken,
        siteLanguageDefault
    } = useContext(UserContext);

    const localization = useLocalization();
    const [enableAssignProducts, setEnableAssignProducts] = useState(false);
    const copyRemixType = new URLSearchParams(window.location.search).get("remixType");

    /* using ref for race condition of dismount useEffect (uncheck selected products and calculate cost for variable EA). */
    const selectedProductsRef = useRef(selectedProducts);
    useEffect(() => {
        selectedProductsRef.current = selectedProducts;
    }, [selectedProducts]);

    const [isVisible, setIsVisible] = useState({
        modal: false,
        discardChangesModal: false,
        hostIDHint: false,
        serialNumberHint: false,
        metadata: false
    });

    const [genericError, setGenericError] = useState(false);
    const [costMessage, setCostMessage] = useState(false);
    const [costFutureMessage, setCostFutureMessage] = useState(false);
    const [qtyZeroError, setQtyZeroError] = useState(false);

    const [minEndDate, setMinEndDate] = useState(new Date());
    const [maxEndDate, setMaxEndDate] = useState(new Date());
    const [datePickerEnabled, setDatePickerEnabled] = useState(false);

    // kendo react excel export
    const [userExcelData, setUserExcelData] = useState([]);
    const [excelFileName] = useState("KSM_EA_ASSIGNEDHOSTS_" + moment(new Date()).format('YYYYMMDD HHmmss'));

    const _exporter = createRef();
    const exportExcel = () => {
        if (_exporter.current) {
            _exporter.current.save();
        }
    };

    // assign host modal variables
    const [hostIDType, setHostIDType] = useState("");

    const [hostModal, setHostModal] = useState("");
    const [hostIsSerial, setHostIsSerial] = useState("");

    const [hostIDLabel, setHostIDLabel] = useState("");
    const [hostIDHint, setHostIDHint] = useState("");
    const [hostIDError, setHostIDError] = useState("");
    const [hostIDPatterns, setHostIDPatterns] = useState([]);
    const [hostIDSuggestions, setHostIDSuggestions] = useState([]);

    const [serialNumberLabel, setSerialNumberLabel] = useState("");
    const [serialNumberHint, setSerialNumberHint] = useState("");
    const [serialNumberError, setSerialNumberError] = useState("");
    const [serialNumberPatterns, setSerialNumberPatterns] = useState([]);
    const [assignedPanelbarView, setAssignedPanelbarView] = useState('hosts');

    const assignedPanelbarViews = useMemo(() => ([
        {
            label: localization.toLanguageString(groupByHostIdKey, mainMessages[siteLanguageDefault][groupByHostIdKey]),
            value: 'hosts',
        },
        {
            label: localization.toLanguageString(groupByProductsKey, mainMessages[siteLanguageDefault][groupByProductsKey]),
            value: 'products',
        }
    ]), [localization, siteLanguageDefault]);

    const groupByProducts = useMemo(() => {
        const groups = {};

        assignedProducts.forEach((host) => {
            const hostId = Array.isArray(host.host_id)
                ? host.host_id.join(', ')
                : typeof host.host_id === 'string'
                    ? host.host_id
                    : '';

            (host.products || []).forEach((product) => {
                const groupByProdNum = typeof product.prod_num_display === 'string'
                    ? product.prod_num_display.trim()
                    : '';
                const groupByProductId = product.product_id !== null && typeof product.product_id !== 'undefined'
                    ? String(product.product_id)
                    : '';
                const groupId = groupByProdNum || groupByProductId;
                if (!groups[groupId]) {
                    const title = [product.prod_num_display, product.description].filter(Boolean).join(' ');
                    groups[groupId] = {
                        panel_bar_title: title,
                        group_id: groupId,
                        products: []
                    };
                }

                groups[groupId].products.push({
                    ...product,
                    host_id: hostId,
                    transaction_id: host.transaction_id,
                });
            });
        });

        return Object.values(groups).sort((a, b) => {
            const titleA = (a.panel_bar_title || '').toLowerCase();
            const titleB = (b.panel_bar_title || '').toLowerCase();
            return titleA.localeCompare(titleB);
        });
    }, [assignedProducts]);

    /*
     * getHostDetails() shows and dynamically renders the assign host modal
    */
    const getHostDetails = () => {
        const selected = selectedProducts.filter(product => product.selected);
        const hostIDType = selected[0]['license_type'].filter(lt => lt.selected)[0].type_val;

        let headers = {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + accessToken
        };

        setGenericError(false);
        setIsLoading(true);

        axios.get(
            config.ea_request_license.HOST,
            {
                headers: headers,
                params: {
                    module: "EA",
                    host_id_type_val: hostIDType
                }
            },
        )
            .then((response) => {
                if (response.status === 200) {
                    let data = typeof response.data === 'undefined' ? [] : response.data;

                    let modalType = data.hostid_type_popup.toUpperCase() || "";
                    let hostIdType = data.host_id_type.toUpperCase() || "";

                    let isSerial = data.get_serial.toUpperCase() || "";

                    let hostLabel = data.host_id_info.asl_prompt || "";
                    let hostHint = data.host_id_info.input_hint || "";
                    let hostError = data.host_id_info.asl_error_message || "";
                    let hostPatterns = data.host_id_info.patterns || [];
                    let serialLabel = data.serial_number_info.asl_prompt || "";
                    let serialHint = data.serial_number_info.input_hint || "";
                    let serialError = data.serial_number_info.asl_error_message || "";
                    let serialPatterns = data.serial_number_info.patterns || [];

                    // list host id suggestions as "host id, alias"
                    // if alias is the same as host id, omit alias from suggestion
                    let hostSuggestions = data.user_profile_tagged_host_ids.map(profile => {
                        let suggestion = profile.node_id !== profile.alias ? profile.node_id + ', ' + profile.alias
                            : profile.node_id;

                        return {
                            ...profile,
                            suggestion: suggestion
                        }
                    }) || [];

                    // handle partials
                    let selected = selectedProducts.filter(product => product.selected);
                    let partials = selectedProducts
                        .filter((product) => {
                            let license = product.license_type.filter(lt => lt.selected);
                            return license && license[0].ea_compatibility === "P"
                        })
                        .map((product) => product.ea_alloc_id);
                    let partialsSelected = selected.every(s => partials.includes(parseInt(s.ea_alloc_id)));

                    // assign variables from data
                    partialsSelected ? setHostModal("PARTIAL")
                        : modalType ? setHostModal(modalType)
                            : setHostModal("");
                    hostIdType ? setHostIDType(hostIdType) : setHostIDType("");
                    isSerial ? setHostIsSerial(isSerial) : setHostIsSerial("");

                    hostLabel ? setHostIDLabel(hostLabel) : setHostIDLabel("");
                    hostHint ? setHostIDHint(hostHint) : setHostIDHint("");
                    hostError ? setHostIDError(hostError) : setHostIDError("");
                    hostSuggestions ? setHostIDSuggestions(hostSuggestions) : setHostIDSuggestions([]);

                    serialLabel ? setSerialNumberLabel(serialLabel) : setSerialNumberLabel("");
                    serialHint ? setSerialNumberHint(serialHint) : setSerialNumberHint("");
                    serialError ? setSerialNumberError(serialError) : setSerialNumberError("");

                    // cast pattern strings to regex
                    // note: regex casting removes addtional \ character for all regex patterns
                    // ex. ^\\w{1,20}$ becomes ^\w{1,20}$
                    hostPatterns.length ? hostPatterns = hostPatterns.map(pattern => new RegExp(pattern))
                        : hostPatterns = [];
                    serialPatterns.length ? serialPatterns = serialPatterns.map(pattern => new RegExp(pattern))
                        : serialPatterns = [];

                    // if optional serial numbers append empty string pattern to valid serial number patterns
                    if (isSerial === "O") {
                        let emptyPattern = new RegExp("^$");
                        serialPatterns.push(emptyPattern);
                    }

                    setHostIDPatterns(hostPatterns);
                    setSerialNumberPatterns(serialPatterns);

                    // show modal
                    setIsLoading(false);
                    setIsVisible(isVisible => ({...isVisible, modal: true}));
                }
            })
            .catch((error) => {
                console.log("ERROR: Failed to GET Host Type Details", error);
                setIsLoading(false);
                setGenericError(true);
            });
    }

    /*
     * calculateCost() recalculates the total cost on end date selections
     * @param {selected} the array of objects of selected products
     * @param {assigned} the array of objects of assigned products
    */
    const calculateCost = (selected = [], assigned = []) => {
        let unSavedCost = 0;
        if (headerInfo.variableoversubflag === 'TRUE') {
            for (const year in futureSubPoolValues) {
                futureSubPoolValues[year].unSavedCost = 0
            }
        }

        let selectedProductsCalculations = selected.length ? selected : [...selectedProducts];
        let assignedProductsCalculations = assigned.length ? assigned : [...assignedProducts];

        // get selected products
        selectedProductsCalculations = selectedProductsCalculations.filter(s => s.selected);

        // calculate cost of selected products
        if (selectedProductsCalculations.length && scalingFactor.length) {
            selectedProductsCalculations.forEach(product => {
                let index = selectedProducts.findIndex(p => p.ea_alloc_id === product.ea_alloc_id);
                if (selectedProducts[index]) {
                    if (selectedProducts[index].unassigned_quantity > 0) {
                        let licenseType = product.license_type.filter(lt => lt.selected);
                        let price = licenseType[0].base_price;
                        let newQty = parseFloat(product.assign_quantity) || 0;
                        const savedCost = multiplyMoney(newQty, price) //scaling factor is 1

                        const newStart = new Date(product.period_start_date)
                        const newEnd = new Date(product.period_end_date)
                        const newDays = Math.ceil(Math.abs(newStart - newEnd) / (1000 * 60 * 60 * 24))
                        const newFactor = parseFloat(getScalingFactor(scalingFactor, newDays))
                        const newCost = multiplyMoney((newFactor * newQty), price)
                        unSavedCost += newCost - savedCost
                        // console.log('savedFactor', 1, 'newFactor', newFactor, 'newEnd', newEnd)
                        // console.log('selected product: newCost', newCost, 'savedCost', savedCost)

                        if (headerInfo.variableoversubflag === 'TRUE') {
                            let newEndDatePoolYear = headerInfo.subs_year
                            for (const year in futureSubPoolValues) {
                                let endDate = new Date(futureSubPoolValues[year].enddate)
                                let startDate = new Date(futureSubPoolValues[year].startdate)
                                if (startDate <= newEnd && newEnd <= endDate) {
                                    newEndDatePoolYear = year
                                }
                            }

                            const newFutureCost = multiplyMoney((newFactor * newQty), price)
                            for (const year in futureSubPoolValues) {
                                if (newEndDatePoolYear >= year) {
                                    // console.log('selected product', newEndDatePoolYear, year, newEndDatePoolYear >= year)
                                    futureSubPoolValues[year].unSavedCost += newFutureCost
                                }
                            }
                            // console.log('selected product newEndDatePoolYear', newEndDatePoolYear, 'newFutureCost', newFutureCost)
                        }
                    }
                }
            });
        }

        // calculate cost of assigned products
        if (assignedProductsCalculations.length && scalingFactor.length) {
            assignedProductsCalculations.forEach(host => {
                host.products.forEach(product => {

                    let licenseType = product.license_type.filter(lt => lt.selected);
                    let price = licenseType[0].base_price;

                    let newCost
                    let newQty = product.assign_quantity
                    const newStart = new Date(product.period_start_date)
                    const newEnd = new Date(product.period_end_date)
                    const newDays = Math.ceil(Math.abs(newStart - newEnd) / (1000 * 60 * 60 * 24))
                    const newFactor = parseFloat(getScalingFactor(scalingFactor, newDays))

                    let savedQty = product.unassigned_quantity
                    const savedStart = new Date(product.saved_period_start_date)
                    const savedEnd = new Date(product.saved_period_end_date)
                    const savedDays = Math.ceil(Math.abs(savedStart - savedEnd) / (1000 * 60 * 60 * 24))
                    const savedFactor = parseFloat(getScalingFactor(scalingFactor, savedDays))

                    const unSavedQtyDiff = newQty - savedQty

                    //logic is to remove saved cost and add new cost
                    if (unSavedQtyDiff > 0) {
                        //handles new qty
                        const savedSelectedCost = multiplyMoney(unSavedQtyDiff, price) //scaling factor is 1
                        const newDiffCost = multiplyMoney((newFactor * unSavedQtyDiff), price)
                        unSavedCost += newDiffCost - savedSelectedCost

                        //handles saved qty
                        const savedCost = multiplyMoney((savedFactor * savedQty), price)
                        if (copyRemixType === "COPYLASTPERIODPRODUCTSONLY" || copyRemixType === "COPYLASTPERIODPRODUCTSANDHOSTS") {
                            newCost = multiplyMoney((newFactor * newQty), price)
                        } 
                        else {
                            newCost = multiplyMoney((newFactor * savedQty), price)
                        }
                        unSavedCost += newCost - savedCost

                        // console.log('unSavedQtyDiff > 0: newDiffCost', newDiffCost, 'savedCost', savedSelectedCost, 'newCost', newCost, 'savedCost', savedCost, 'price', price, 'unsavedqtycost', unSavedCost)
                    } else if (unSavedQtyDiff === 0) {

                        //handles saved qty
                        const savedCost = multiplyMoney((savedFactor * savedQty), price)
                        newCost = multiplyMoney((newFactor * savedQty), price)
                        unSavedCost += newCost - savedCost

                        // console.log('unSavedQtyDiff === 0: newCost', newCost, 'savedCost', savedCost, 'price', price, 'unsavedqtycost', unSavedCost)
                    } else {
                        const savedCost = multiplyMoney((savedFactor * savedQty), price)
                        newCost = multiplyMoney((newFactor * newQty), price)
                        const newSelectedCost = multiplyMoney(-unSavedQtyDiff, price)
                        unSavedCost += newCost + newSelectedCost - savedCost

                        // console.log('unSavedQtyDiff < 0: newCost', newCost, 'newSelectedCost', newSelectedCost, 'savedCost', savedCost, 'price', price, 'unsavedqtycost', unSavedCost)
                    }
                    
                    let totalCost = multiplyMoney((newFactor * newQty), price)
                    product.total_cost = totalCost
                
                    if (headerInfo.variableoversubflag === 'TRUE') {
                        let newEndDatePoolYear = headerInfo.subs_year
                        let savedEndDatePoolYear = headerInfo.subs_year
                        for (const year in futureSubPoolValues) {
                            let endDate = new Date(futureSubPoolValues[year].enddate)
                            let startDate = new Date(futureSubPoolValues[year].startdate)
                            if (startDate <= newEnd && newEnd <= endDate) {
                                newEndDatePoolYear = year
                            }
                            if (startDate <= savedEnd && savedEnd <= endDate) {
                                savedEndDatePoolYear = year
                            }
                        }

                        const savedFutureCost = multiplyMoney((savedFactor * savedQty), price)
                        const newFutureCost = multiplyMoney((newFactor * newQty), price)

                        for (const year in futureSubPoolValues) {
                            if (savedEndDatePoolYear >= year) {
                                futureSubPoolValues[year].unSavedCost -= savedFutureCost
                            }

                            if (newEndDatePoolYear >= year) {
                                futureSubPoolValues[year].unSavedCost += newFutureCost
                            }
                        }
                        // console.log('newEndDatePoolYear', newEndDatePoolYear, 'newFutureCost', newFutureCost, 'savedEndDatePoolYear', savedEndDatePoolYear, 'savedFutureCost', savedFutureCost)
                    }

                    // update cost in excel data
                    let index = userExcelData.findIndex(e => e.ea_alloc_id === product.ea_alloc_id);
                    if (index > -1) {
                        userExcelData[index] = {
                            ...userExcelData[index],
                            total_cost: totalCost,
                        };
                    }
                })
            });
        }

        if (unSavedCost <= headerInfo?.remaining_pool - headerInfo.selected_sub_pool) {
            stepperItems[0].disabled = false;
            stepperItems[2].disabled = false;
            setStepperItems(stepperItems);
            setCostMessage(false);
        } else {
            stepperItems[0].disabled = true;
            stepperItems[2].disabled = true;
            setStepperItems(stepperItems);
            setCostMessage(true);
        }
        setUnSavedPool(unSavedCost)

        if (headerInfo.variableoversubflag === 'TRUE') {
            let exceed = false
            for (const year in futureSubPoolValues) {
                const remaining = futureSubPoolValues[year].availablesubscriptionpool
                const selected = futureSubPoolValues[year].selectedsubscriptionpool
                const unSaved = futureSubPoolValues[year].unSavedCost
                if (unSaved > remaining - selected) {
                    exceed = true
                }
            }
            // console.table(futureSubPoolValues)
            // console.log('future subpool exceed', exceed)
            if (exceed) {
                stepperItems[0].disabled = true;
                stepperItems[2].disabled = true;
            } else {
                stepperItems[0].disabled = false;
                stepperItems[2].disabled = false;
            }
            setStepperItems(stepperItems);
            setCostFutureMessage(exceed)
        }

        return unSavedCost;
    }

    // calculate cost on any change
    useEffect(() => {
        setCostMessage(false);
        if ((selectedProducts.length || assignedProducts.length) && scalingFactor) {
            calculateCost([], []);
        }
    }, [selectedProducts, assignedProducts, futureSubPoolValues, headerInfo]); // eslint-disable-line react-hooks/exhaustive-deps

    //un selected any checked row from selected products and recalculate cost when leaving step 2
    useEffect(() => {
        return () => {
            let newSelectedProducts = selectedProductsRef.current
            for (const product of newSelectedProducts) {
                product.selected = false
            }
            setSelectedProducts([...newSelectedProducts])
            if (copyRemixType !== "COPYLASTPERIODPRODUCTSONLY" && copyRemixType !== "COPYLASTPERIODPRODUCTSANDHOSTS") {
                calculateCost()
            }
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // get minimum end date header restriction
    useEffect(() => {
        let eAEndDate = new Date(headerInfo.ea_end_date);
        let min = new Date();
        min.setDate(min.getDate() + eAMinDuration);

        if (min < eAEndDate) {
            // case: today + ea min duration < ea end date,
            //  set minimum restriction to today + ea min duration
            //  set maximum restriction to ea end date
            setMinEndDate(min);
            setMaxEndDate(eAEndDate);
            setDatePickerEnabled(true);
        } else {
            // case: today + ea min duration >= ea end date,
            // disable date picker
            // set maximum restriction to ea end date
            setDatePickerEnabled(false);
            setMaxEndDate(eAEndDate);
        }
    }, [headerInfo, eAMinDuration]);

    // update excel data and remove any errors
    useEffect(() => {
        setQtyZeroError(false);
        if (assignedProducts.length) {
            let updates = [];
            assignedProducts.forEach(host => {
                host.products.forEach(product => {
                    let index = updates.findIndex(e => e.ea_alloc_id === product.ea_alloc_id);
                    let licenseType = product.license_type.filter(lt => lt.selected);
                    let price = licenseType[0].base_price;
                    let hostId = Array.isArray(host.host_id) ? host.host_id.join(', ')
                        : typeof host.host_id === "string" ? host.host_id
                            : "";
                    if (index === -1) {
                        updates.push({
                            ea_alloc_id: product.ea_alloc_id,
                            prod_num_display: product.prod_num_display,
                            description: product.description,
                            license_type: licenseType[0].license_type,
                            assign_quantity: product.assign_quantity,
                            period_start_date: moment(product.period_start_date).format('MM/DD/YYYY'),
                            period_end_date: moment(product.period_end_date).format('MM/DD/YYYY'),
                            host_id: hostId,
                            cost_per_unit: price
                        });
                    }
                })
            })
            setUserExcelData(updates);
        }
    }, [assignedProducts]);

    //enable assign products button
    useEffect(()=>{
        const selected = selectedProducts.filter(product => product.selected && product.unassigned_quantity > 0);
        const isValidQty = selected.every(product => typeof product.assign_quantity == "number" && product.assign_quantity > 0);
        setEnableAssignProducts(selected.length && isValidQty);
    }, [selectedProducts]);

    // delete cart and undo copy products and hosts, then return to summary page
    const discardChanges = () => {
        setIsVisible(isVisible => ({...isVisible, discardChangesModal: false}));
        setIsLoading(true);
        setGenericError(false);

        const headers = {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + accessToken
        };

        let periodSelection = new URLSearchParams(window.location.search).get("show");
        let eaProducts = [];

        selectedProducts.forEach(product => {
            eaProducts.push({
                ea_alloc_id: product.ea_alloc_id,
                status: "SELECTED"
            });
        });

        assignedProducts.forEach(host => {
            if (host.products && Array.isArray(host.products)) {
                host.products.forEach(product => {
                    eaProducts.push({
                        ea_alloc_id: product.ea_alloc_id,
                        status: "ASSIGNED"
                    });
                });
            }
        });

        let payload = {
            module: "EA",
            sub_module: "allocations",
            action: "DISCARD",
            input_json: [
                {
                    ea_id: headerInfo?.ea_id,
                    period_selection: periodSelection,
                    ea_products: eaProducts
                }
            ]
        }

        axios.post(
            config.ea_request_license.SAVE_DELETE,
            payload,
            { headers }
        ).then(response => {
            if (response.status === 200) {
                history.push('/ea-summary');
            }
        }).catch(error => {
            console.log('ERROR: Failed to discard changes', error);
            setGenericError(true);
        }).finally(() => {
            setIsLoading(false);
        });
    };

    return (
        <>
            <div className={'k-h5'}>
                {localization.toLanguageString(productsHostKey, mainMessages[siteLanguageDefault][productsHostKey])}
            </div>

            {!!assignedProducts.length && (<>
                <div className={"k-h5"}>
                    {localization.toLanguageString(myAssignedProductsKey, mainMessages[siteLanguageDefault][myAssignedProductsKey])}
                </div>

                <div style={{
                    display: 'flex',
                    marginBottom: '0.5rem'
                }}>
                    <RadioGroup
                        onChange={(e) => {
                            setAssignedPanelbarView(e.value);
                        }}
                        value={assignedPanelbarView}
                        data={assignedPanelbarViews}
                        layout={'horizontal'}
                    />
                </div>

                <PanelBar
                    expandMode={'multiple'}
                    className={'ksm-panelbar-default'}
                >
                    {assignedPanelbarView === 'hosts' && assignedProducts.map((assignedProduct) => {
                        if (assignedProduct.products && assignedProduct.products.length) {
                            return <PanelBarItem
                                title={<PanelBarHeader
                                    title={assignedProduct.panel_bar_title}
                                    transactionID={assignedProduct.transaction_id}
                                    assignedProducts={assignedProducts}
                                    saveRemovals={saveRemovals}
                                />}
                                key={assignedProduct.transaction_id}
                            >
                                {(copyRemixType === "COPYLASTPERIODPRODUCTSONLY" || copyRemixType === "COPYLASTPERIODPRODUCTSANDHOSTS") ? (
                                    <CopyHostsGrid
                                        selectedProducts={selectedProducts}
                                        assignedProducts={assignedProducts}
                                        setAssignedProducts={setAssignedProducts}
                                        assignedProduct={assignedProduct}
                                        headerInfo={headerInfo}
                                        saveRemovals={saveRemovals}
                                        remixType={remixType}
                                        calculateCost={calculateCost}
                                        minEndDate={minEndDate}
                                        maxEndDate={maxEndDate}
                                        datePickerEnabled={datePickerEnabled}
                                        unsavedAssignedChanges={unsavedAssignedChanges}
                                        setUnsavedAssignedChanges={setUnsavedAssignedChanges}
                                    />
                                ) :  
                                (
                                    <HostGrid
                                        selectedProducts={selectedProducts}
                                        assignedProducts={assignedProducts}
                                        setAssignedProducts={setAssignedProducts}
                                        assignedProduct={assignedProduct}
                                        headerInfo={headerInfo}
                                        saveRemovals={saveRemovals}
                                        remixType={remixType}
                                        calculateCost={calculateCost}
                                        minEndDate={minEndDate}
                                        maxEndDate={maxEndDate}
                                        datePickerEnabled={datePickerEnabled}
                                        unsavedAssignedChanges={unsavedAssignedChanges}
                                        setUnsavedAssignedChanges={setUnsavedAssignedChanges}
                                    />
                                )}
                                
                            </PanelBarItem>
                        } else {
                            return <></>
                        }
                    })}
                    {assignedPanelbarView === 'products' && groupByProducts.map((assignedProduct) => {
                        if (assignedProduct.products && assignedProduct.products.length) {
                            return <PanelBarItem
                                title={
                                    <div 
                                        className='k-h5' 
                                        style={{
                                            color: 'white',
                                            margin: '0'
                                        }}
                                    >
                                        {assignedProduct.panel_bar_title}
                                    </div>
                                }
                                key={assignedProduct.group_id}
                            >
                                {(copyRemixType === "COPYLASTPERIODPRODUCTSONLY" || copyRemixType === "COPYLASTPERIODPRODUCTSANDHOSTS") ? (
                                    <CopyProductGrid
                                        selectedProducts={selectedProducts}
                                        assignedProducts={assignedProducts}
                                        setAssignedProducts={setAssignedProducts}
                                        assignedProduct={assignedProduct}
                                        headerInfo={headerInfo}
                                        saveRemovals={saveRemovals}
                                        remixType={remixType}
                                        calculateCost={calculateCost}
                                        minEndDate={minEndDate}
                                        maxEndDate={maxEndDate}
                                        datePickerEnabled={datePickerEnabled}
                                        unsavedAssignedChanges={unsavedAssignedChanges}
                                        setUnsavedAssignedChanges={setUnsavedAssignedChanges}
                                    />
                                ) : (
                                    <ProductGrid
                                        selectedProducts={selectedProducts}
                                        assignedProducts={assignedProducts}
                                        setAssignedProducts={setAssignedProducts}
                                        assignedProduct={assignedProduct}
                                        headerInfo={headerInfo}
                                        saveRemovals={saveRemovals}
                                        remixType={remixType}
                                        calculateCost={calculateCost}
                                        minEndDate={minEndDate}
                                        maxEndDate={maxEndDate}
                                        datePickerEnabled={datePickerEnabled}
                                        unsavedAssignedChanges={unsavedAssignedChanges}
                                        setUnsavedAssignedChanges={setUnsavedAssignedChanges}
                                    />
                                )}
                            </PanelBarItem>
                        } else {
                            return <></>
                        }
                    })}
                </PanelBar>

                <Row style={{marginBottom: '0.938rem'}}>
                    <Col>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'start',
                                flexWrap: 'wrap',
                                gap: '0.5rem'
                            }}
                        >
                            <Button
                                themeColor={"primary"}
                                size={"large"}
                                fillMode={"outline"}
                                type={"button"}
                                onClick={() => {
                                    changeStepActivation(0);
                                }}
                            >
                                {localization.toLanguageString(backKey, mainMessages[siteLanguageDefault][backKey])}
                            </Button>
                            {(copyRemixType === "COPYLASTPERIODPRODUCTSONLY" || copyRemixType === "COPYLASTPERIODPRODUCTSANDHOSTS") && showDiscardChanges && !fromRequestAgain && (
                                <Button
                                    themeColor={"primary"}
                                    size={"large"}
                                    fillMode={"outline"}
                                    type={"button"}
                                    onClick={() => setIsVisible(isVisible => ({...isVisible, discardChangesModal: true}))}
                                >
                                    {localization.toLanguageString(discardChangesKey, mainMessages[siteLanguageDefault][discardChangesKey])}
                                </Button>
                            )}
                            <Button
                                themeColor={"primary"}
                                size={"large"}
                                fillMode={"outline"}
                                type={"button"}
                                disabled={unsavedAssignedChanges.size === 0 || costMessage || costFutureMessage}
                                onClick={() => {
                                    if (copyRemixType === "COPYLASTPERIODPRODUCTSONLY" || copyRemixType === "COPYLASTPERIODPRODUCTSANDHOSTS") {
                                        saveAssignmentsForCopyProductsFlow(1);
                                    } else {
                                        saveAssignments(1);
                                    }
                                }}
                            >
                                {localization.toLanguageString(saveChangesKey, mainMessages[siteLanguageDefault][saveChangesKey])}
                            </Button>
                        </div>
                    </Col>
                    {qtyZeroError && (
                        <Col>
                            <Alert
                                type={'error'}
                                title={localization.toLanguageString(genericErrorTitleKey, mainMessages[siteLanguageDefault][genericErrorTitleKey])}
                                message={localization.toLanguageString(quantityZeroKey, mainMessages[siteLanguageDefault][quantityZeroKey])}
                            />
                        </Col>
                    )}
                    <Col>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'end',
                                flexWrap: 'wrap',
                                gap: '0.5rem'
                            }}
                        >
                            <Button
                                themeColor={"primary"}
                                size={"large"}
                                fillMode={"solid"}
                                type={"button"}
                                disabled={assignedProducts.length === 0 || costMessage || costFutureMessage}
                                onClick={() => {
                                    let hasZeroQty = false;
                                    assignedProducts.forEach(host => {
                                        host.products.forEach(product => {
                                            if (product.assign_quantity === 0) {
                                                hasZeroQty = true;
                                            }
                                        });
                                    });
                                    if (hasZeroQty) {
                                        setQtyZeroError(true);
                                    } else {
                                        setQtyZeroError(false);
                                        changeStepActivation(2);
                                    }
                                }}
                            >
                                {localization.toLanguageString(continueKey, mainMessages[siteLanguageDefault][continueKey])}
                            </Button>

                            <div>
                                <Tooltip
                                    anchorElement="target"
                                    showCallout={true}
                                    parentTitle={true}
                                    openDelay={0}
                                    position="top"
                                >
                                    <Button
                                        title={localization.toLanguageString(exportSelectionsKey, mainMessages[siteLanguageDefault][exportSelectionsKey])}
                                        themeColor={"tertiary"}
                                        fillMode={"solid"}
                                        size={"large"}
                                        onClick={() => {
                                            exportExcel();
                                        }}
                                        svgIcon={fileExcelIcon}
                                    />
                                </Tooltip>
                            </div>
                            <ExcelExport
                                data={userExcelData}
                                fileName={excelFileName}
                                ref={_exporter}
                            >
                                <ExcelExportColumn
                                    field="prod_num_display"
                                    title={localization.toLanguageString(productNumberKey, mainMessages[siteLanguageDefault][productNumberKey])}
                                />
                                <ExcelExportColumn
                                    field="description"
                                    title={localization.toLanguageString(descriptionKey, mainMessages[siteLanguageDefault][descriptionKey])}
                                />
                                <ExcelExportColumn
                                    field="license_type"
                                    title={localization.toLanguageString(licenseTypeKey, mainMessages[siteLanguageDefault][licenseTypeKey])}
                                />
                                <ExcelExportColumn
                                    field="assign_quantity"
                                    title={localization.toLanguageString(assignQtyKey, mainMessages[siteLanguageDefault][assignQtyKey])}
                                />
                                <ExcelExportColumn
                                    field="period_start_date"
                                    title={localization.toLanguageString(startDateKey, mainMessages[siteLanguageDefault][startDateKey])}
                                />
                                <ExcelExportColumn
                                    field="period_end_date"
                                    title={localization.toLanguageString(endDateKey, mainMessages[siteLanguageDefault][endDateKey])}
                                />
                                <ExcelExportColumn
                                    field="host_id"
                                    title={localization.toLanguageString(hostIdKey, mainMessages[siteLanguageDefault][hostIdKey])}
                                />
                                <ExcelExportColumn
                                    field="cost_per_unit"
                                    title={localization.toLanguageString(costPerUnitKey, mainMessages[siteLanguageDefault][costPerUnitKey])}
                                />
                                <ExcelExportColumn
                                    field="total_cost"
                                    title={localization.toLanguageString(totalCostKey, mainMessages[siteLanguageDefault][totalCostKey])}
                                />
                            </ExcelExport>
                        </div>
                    </Col>
                </Row>

                <div className={'k-hr'} style={{margin: '1rem 0 1rem 0'}}/>
            </>)}

            {!!selectedProducts
                    .filter((product) => product.unassigned_quantity > 0)
                    .length &&
                (<>
                    <div className={'k-h5'}>
                        {localization.toLanguageString(myCartKey, mainMessages[siteLanguageDefault][myCartKey])}
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'start',
                        }}
                    >
                        <Button
                            themeColor={"primary"}
                            size={"large"}
                            fillMode={"solid"}
                            type={"button"}
                            disabled={!enableAssignProducts || costMessage || costFutureMessage}
                            onClick={() => {
                                getHostDetails();
                            }}
                            style={{
                                marginBottom: "0.5rem"
                            }}
                        >
                            <Text
                                textkey={productsHostKey}
                                textdefault={mainMessages[siteLanguageDefault][productsHostKey]}
                            />
                        </Button>
                    </div>
                    <div
                        style={{
                            marginBottom: '0.5rem'
                        }}
                    >
                        <MyProductsGrid
                            selectedProducts={selectedProducts}
                            setSelectedProducts={setSelectedProducts}
                            headerInfo={headerInfo}
                            remixType={remixType}
                            calculateCost={calculateCost}
                            minEndDate={minEndDate}
                            maxEndDate={maxEndDate}
                            datePickerEnabled={datePickerEnabled}
                        />
                    </div>
                </>)}

            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                marginTop: '0.5rem'
            }}>

                {costMessage &&
                    <Alert
                        type={'error'}
                        title={localization.toLanguageString(poolOverKey, mainMessages[siteLanguageDefault][poolOverKey])}
                        message={localization.toLanguageString(durationIncreaseCostKey, mainMessages[siteLanguageDefault][durationIncreaseCostKey])}
                    />
                }

                {costFutureMessage &&
                    <Alert
                        type={'error'}
                        title={localization.toLanguageString(endDateExceedPoolKey, mainMessages[siteLanguageDefault][endDateExceedPoolKey])}
                        message={localization.toLanguageString(endDateNotPossibleKey, mainMessages[siteLanguageDefault][endDateNotPossibleKey])}
                    />
                }

                {showQuantityConsumedError &&

                    <Alert
                        type={'error'}
                        title={localization.toLanguageString(quantityConsumedKey, mainMessages[siteLanguageDefault][quantityConsumedKey])}
                        message={localization.toLanguageString(quantityConsumedSessionKey, mainMessages[siteLanguageDefault][quantityConsumedSessionKey])}
                    />
                }

                {genericError &&
                    <Alert
                        type={'error'}
                        title={localization.toLanguageString(genericErrorTitleKey, mainMessages[siteLanguageDefault][genericErrorTitleKey])}
                        message={localization.toLanguageString(contactUsHeaderKey, mainMessages[siteLanguageDefault][contactUsHeaderKey])}
                    />
                }
            </div>

            {isVisible.modal &&
                <AssignProductsModal
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                    setHeaderInfo={setHeaderInfo}
                    setSelectedState={()=>{}}
                    assignedProducts={assignedProducts}
                    setAssignedProducts={setAssignedProducts}
                    selectedProducts={selectedProducts}
                    setSelectedProducts={setSelectedProducts}
                    headerInfo={headerInfo}
                    hostIDType={hostIDType}
                    hostModal={hostModal}
                    setHostModal={setHostModal}
                    hostIsSerial={hostIsSerial}
                    setHostIsSerial={setHostIsSerial}
                    hostIDLabel={hostIDLabel}
                    setHostIDLabel={setHostIDLabel}
                    hostIDHint={hostIDHint}
                    setHostIDHint={setHostIDHint}
                    hostIDError={hostIDError}
                    hostIDPatterns={hostIDPatterns}
                    setHostIDPatterns={setHostIDPatterns}
                    hostIDSuggestions={hostIDSuggestions}
                    serialNumberLabel={serialNumberLabel}
                    setSerialNumberLabel={setSerialNumberLabel}
                    serialNumberHint={serialNumberHint}
                    setSerialNumberHint={setSerialNumberHint}
                    serialNumberError={serialNumberError}
                    serialNumberPatterns={serialNumberPatterns}
                    setSerialNumberPatterns={setSerialNumberPatterns}
                    isVisible={isVisible}
                    setIsVisible={setIsVisible}
                    futureSubPoolValues={futureSubPoolValues}
                    setFutureSubPoolValues={setFutureSubPoolValues}
                />
            }

            {isVisible.discardChangesModal &&
                <DiscardChangesModal
                    isVisible={isVisible}
                    setIsVisible={setIsVisible}
                    discardChanges={discardChanges}
                />
            }
        </>
    )
}

export default AssignProducts